import { ObjectUtil, StringUtil } from 'kuaidong-lib'
import { ISchoolOperateType, MenuRole, Teacher, TeacherRoleType, UserRoleInfo } from 'kuaidong-model'

export class SchoolPermissionService {
  constructor(public teacher: Teacher | null = null) {}

  public static buildSchoolVerifyPath(returnFullPath: string) {
    const queryStr = StringUtil.buildQueryString({
      verify: true,
      returnPath: returnFullPath,
    })
    return `/school-manage-annually-init/school-basic?${queryStr}`
  }

  // 是否是付费校
  get isPaid() {
    return (
      this.teacher?.school.operate_type === ISchoolOperateType.Paid ||
      this.teacher?.school.operate_type === ISchoolOperateType.PaidExport
    )
  }

  // 付费校且开启导出名单功能
  get isPaidExport() {
    return this.teacher?.school.operate_type === ISchoolOperateType.PaidExport
  }

  // Enable university.
  get isUniversity() {
    return !!this.teacher?.school.is_tzy_university_version
  }

  // 是否是官方认证校
  // @deprecated
  get isOfficialAuh() {
    return !!this.teacher?.school.verified
  }

  // 是否是官方认证校
  get isOfficialAuthed() {
    return !!this.teacher?.school.verified
  }

  // 是否是官方认证校且到验证方式是通过学籍号
  get isVerifyByStudentCode() {
    return this.teacher?.school.verify_method === 'student_code'
  }

  // 是否是官方认证校且验证方式为合作账号
  get isVerifyByAccount() {
    return this.teacher?.school.verify_method === 'account'
  }

  // 是否是运动会校
  get isSportMeetingSchool() {
    return this.teacher?.school_sport_meet
  }

  get canVisitStudentCodePage() {
    return this.isOfficialAuthed && this.isVerifyByStudentCode
  }

  get canVisitStudentAccountPage() {
    return this.isOfficialAuthed && this.isVerifyByAccount
  }

  get canVisitUserAccountPage() {
    return !!this.teacher?.user_account_permission
  }
}

export class TeacherPermissionService {
  constructor(public userRoleInfo: UserRoleInfo | null = null, public teacher: Teacher | null = null) {}
  get role() {
    return this.teacher?.role || 'others'
  }

  //  是否是管理员
  get isAdmin() {
    return !!this.userRoleInfo?.is_admin
  }

  // 超级管理员
  get isSuperAdmin() {
    return !!this.userRoleInfo?.is_super_admin
  }

  get isSuperAdminOrAdmin() {
    return this.isAdmin || this.isSuperAdmin
  }

  //  普通教师
  get isNormal() {
    return !this.isSuperAdminOrAdmin
  }

  get isRolePeManager() {
    return this.teacher?.role === 'pe_manager'
  }

  get isRoleSchoolManager() {
    return this.teacher?.role === 'school_manager'
  }

  get isPeTeacher() {
    return this.teacher?.role === 'pe_teacher'
  }

  get isOtherTeacher() {
    return this.teacher?.role === 'others'
  }

  // 体智云大学版的老师
  get isCollegeTeacher() {
    return !!(this.teacher && this.teacher.school.is_tzy_university_version)
  }

  isPermittedRole(roles: TeacherRoleType[]) {
    return !!roles.find((role) => role === this.teacher?.role)
  }
}

export class PagePermissionService {
  schoolPermissionService: SchoolPermissionService
  teacherPermissionService: TeacherPermissionService

  constructor(
    userRoleInfo: UserRoleInfo | null = null,
    teacher: Teacher | null = null,
    public menuRole: MenuRole | null = null
  ) {
    this.schoolPermissionService = new SchoolPermissionService(teacher)
    this.teacherPermissionService = new TeacherPermissionService(userRoleInfo, teacher)
  }

  get competitionManagePage() {
    return {
      showExportBtn: true,
    }
  }
  // 学生管理页面
  get studentAuthPage() {
    return {
      // 当前管理员和普通用户都可以上传
      canAdjustStudentClass: true,

      canUploadStudentCodeList: true,

      canExportExcel: true,

      canDeleteOrUnbindStudentCode: this.teacherPermissionService.isSuperAdminOrAdmin,

      canDeleteAccountOrUnbindPhone: this.teacherPermissionService.isSuperAdminOrAdmin,

      canAddStudentAccount: true,
      canDelStudentAccount: this.teacherPermissionService.isSuperAdminOrAdmin,
      canUnBundlingStudentPhone: this.teacherPermissionService.isSuperAdminOrAdmin,
      canResetPassword: true,
      canChangeClass: true,
    }
  }

  // 班级管理
  get classManageListPage() {
    return {
      // 添加单个学生名单
      canAddStudentCodeList: true,
      canDelClass: !!this.teacherPermissionService.isSuperAdminOrAdmin,

      //  加入班级
      canJoinClass: true,
      //  创建班级
      canCreateClass: this.schoolPermissionService.isOfficialAuthed
        ? this.teacherPermissionService.isSuperAdminOrAdmin
        : true,
    }
  }

  //  体质测试
  get examPage() {
    if (this.menuRole === null) return null
    const examConfig = this.menuRole['体质测试']
    const schoolExam = examConfig['学校跟测']
    return {
      schoolExam: {
        addStudent: this.hasRole(schoolExam['创建学生']),

        addExam: this.hasRole(schoolExam['创建测试']),
        delExam: this.hasRole(schoolExam['删除测试']),
        editExam: this.hasRole(schoolExam['修改测试']),
        viewExam: this.hasRole(schoolExam['查看测试']),

        inputScore: this.hasRole(schoolExam['录入成绩']),
        editScore: this.hasRole(schoolExam['修改成绩']),
        downLoad: this.hasRole(schoolExam['下载数据']),

        viewReport: this.hasRole(schoolExam['查看报告']),
        canPublish: this.hasRole(schoolExam['发布给家长']),
      },
      studentExam: {
        all: true,
      },
    }
  }

  //  判断是否有角色权限
  hasRole(roles: TeacherRoleType[] | undefined) {
    if (roles === undefined) {
      return true
    }
    // 管理员或者残疾管理员默认拥有所有操作权利
    if (this.teacherPermissionService.isSuperAdminOrAdmin) {
      return true
    }

    if (this.teacherPermissionService.teacher && this.teacherPermissionService.teacher.role) {
      return roles.includes(this.teacherPermissionService.teacher.role)
    }

    return false
  }
}

export class ModulePermissionService {
  constructor(private tps: TeacherPermissionService, private roleConfig: MenuRole = {}) {}

  get clazzMgt() {
    const rolesMap = this.getRolesMap('校园管理.班级管理')

    return {
      edit: this.hasPermission(rolesMap['编辑']),
      create: this.hasPermission(rolesMap['新建']),
      remove: this.hasPermission(rolesMap['解散班级']),
      updateSettings: this.hasPermission(rolesMap['设置班级数量']),
    }
  }

  get schoolMgt() {
    const rolesMap = this.getRolesMap('校园管理.学校管理')

    return {
      view: this.hasPermission(rolesMap['查看']),
      edit: this.hasPermission(rolesMap['编辑']),
    }
  }

  get teacherMgt() {
    const rolesMap = this.getRolesMap('校园管理.教师管理')

    return {
      view: this.hasPermission(rolesMap['查看']),
      viewDetails: this.hasPermission(rolesMap['查看明细']),
      edit: this.hasPermission(rolesMap['编辑其他人']),
      editMine: this.hasPermission(rolesMap['编辑自己']),
      editRole: this.hasPermission(rolesMap['修改角色']),
      create: this.hasPermission(rolesMap['添加教师']),
      remove: this.hasPermission(rolesMap['删除教师']),
    }
  }

  get studentAuthMgt() {
    const rolesMap = this.getRolesMap('校园管理.学生认证管理')

    return {
      enable: this.hasPermission(rolesMap['开启认证']),
      disable: this.hasPermission(rolesMap['关闭认证']),
      addStudent: this.hasPermission(rolesMap['新增学生']),
      changeClazz: this.hasPermission(rolesMap['调整班级']),
      unbindRollNo: this.hasPermission(rolesMap['解除绑定']),
      removeRollNo: this.hasPermission(rolesMap['删除名单']),
      removeRollNoAll: this.hasPermission(rolesMap['删除全校名单']),
      import: this.hasPermission(rolesMap['导入名单']),
      export: this.hasPermission(rolesMap['导出数据']),
    }
  }

  get gymnasticsCustom() {
    const rolesMap = this.getRolesMap('广播体操.自编操')

    return {
      view: this.hasPermission(rolesMap['查看']),
      create: this.hasPermission(rolesMap['布置']),
      upload: this.hasPermission(rolesMap['上传']),
      remove: this.hasPermission(rolesMap['删除其他']),
      removeMine: this.hasPermission(rolesMap['删除自己']),
    }
  }

  get officalActivity() {
    const rolesMap = this.getRolesMap('活动管理.官方活动')

    return {
      create: this.hasPermission(rolesMap['新建']),
      updateMine: this.hasPermission(rolesMap['修改自己']),
      updateOthers: this.hasPermission(rolesMap['修改他人']),
      deleteMine: this.hasPermission(rolesMap['删除自己']),
      deleteOthers: this.hasPermission(rolesMap['删除他人']),
    }
  }

  get processEvaluation() {
    const rolesMap = this.getRolesMap('体育成绩评价.方案配置')

    return {
      check: this.hasPermission(rolesMap['查看']),
      create: this.hasPermission(rolesMap['新增']),
      delete: this.hasPermission(rolesMap['删除']),
      update: this.hasPermission(rolesMap['修改']),
    }
  }

  get semesterConfig() {
    const rolesMap = this.getRolesMap('体育成绩评价.学期设置')

    return {
      check: this.hasPermission(rolesMap['查看']),
      create: this.hasPermission(rolesMap['新增']),
      delete: this.hasPermission(rolesMap['删除']),
      update: this.hasPermission(rolesMap['修改']),
    }
  }

  get evaluationSource() {
    const rolesMap = this.getRolesMap('体育成绩评价.数据来源')

    return {
      check: this.hasPermission(rolesMap['查看']),
      update: this.hasPermission(rolesMap['修改']),
    }
  }

  get homeworkPermission() {
    const rolesMap = this.getRolesMap('体育作业.作业设置')

    return {
      redo: this.hasPermission(rolesMap['补作业']),
    }
  }

  public getRoles(key: string): TeacherRoleType[] | undefined {
    return ObjectUtil.get(this.roleConfig, key) || []
  }

  public getRolesMap(key: string): { [index: string]: TeacherRoleType[] | undefined } {
    return ObjectUtil.get(this.roleConfig, key) || {}
  }

  public hasPermission(roles?: TeacherRoleType[]) {
    if (!roles) {
      return true
    }

    if (this.tps.isSuperAdminOrAdmin) {
      return true
    }

    if (this.tps.teacher?.role) {
      return roles.includes(this.tps.teacher.role)
    }

    return false
  }
}

export interface IPermission {
  Teacher: TeacherPermissionService
  School: SchoolPermissionService
  Page: PagePermissionService
  t: TeacherPermissionService
  s: SchoolPermissionService
  m: ModulePermissionService
}

export class PermissionService {
  teacherPermissionService: TeacherPermissionService
  schoolPermissionService: SchoolPermissionService
  modulePermissionService: ModulePermissionService
  pagePermissionService: PagePermissionService

  constructor(
    userRoleInfo: UserRoleInfo | null = null,
    teacher: Teacher | null = null,
    menuRole: MenuRole | null = null
  ) {
    this.teacherPermissionService = new TeacherPermissionService(userRoleInfo, teacher)
    this.schoolPermissionService = new SchoolPermissionService(teacher)
    this.modulePermissionService = new ModulePermissionService(this.teacherPermissionService, menuRole || {})
    this.pagePermissionService = new PagePermissionService(userRoleInfo, teacher, menuRole)
  }

  buildPermissions(): IPermission {
    return {
      Teacher: this.teacherPermissionService,
      School: this.schoolPermissionService,
      Page: this.pagePermissionService,
      t: this.teacherPermissionService,
      s: this.schoolPermissionService,
      m: this.modulePermissionService,
    }
  }
}

interface IVue {
  toast(text: string): void
  $p: IPermission
}

export function teacherRole(roles: TeacherRoleType[], reject: ((roles: TeacherRoleType[]) => void) | null = null) {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const fun = target[propertyKey]
    descriptor.value = function (...args: any) {
      if ((this as IVue).$p.Teacher.isPermittedRole(roles)) {
        fun.apply(this, args)
      } else {
        if (reject) {
          reject.call(this, roles)
        } else {
          ;(this as IVue).toast('您暂无更改权限')
        }
      }
    }
  }
}
